/**
 * Reports — R5 (2026-05-17) — landing page for the reports suite.
 *
 * Card grid of all reports available to the operator. Each report has its
 * own permission gate handled by RequireRole on the individual route — this
 * page just shows the menu. The PII-bearing Verified Members card is visually
 * flagged so operators understand the access tier.
 */

import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { isFeatureEnabled } from "../utils/featureFlags";
import { useAuth } from "../contexts/AuthContext";
import {
  BuildingOffice2Icon,
  UserGroupIcon,
  ClipboardDocumentCheckIcon,
  ChartBarSquareIcon,
  IdentificationIcon,
  LockClosedIcon,
  PhoneIcon,
  ShoppingBagIcon,
  ChatBubbleLeftRightIcon,
} from "@heroicons/react/24/outline";

interface ReportCard {
  title: string;
  description: string;
  path: string;
  icon: React.ComponentType<{ className?: string }>;
  isPii?: boolean;
  isComingSoon?: boolean;
  /** Shown only when this returns true (role / feature gated cards). */
  visible?: (role: string | undefined) => boolean;
}

const REPORTS: ReportCard[] = [
  {
    title: "SMS messages held past retention",
    description:
      "Text messages older than 24 months that open work still keeps: who or what holds each one, its age, and how to clear it. Admins only.",
    path: "/reports/sms-retention-held",
    icon: ChatBubbleLeftRightIcon,
    visible: (role) =>
      isFeatureEnabled("operatorSmsConversations") &&
      ["superadmin", "super_admin", "admin"].includes(
        (role ?? "").toLowerCase(),
      ),
  },
  {
    title: "Events by Organization",
    description:
      "Events filtered by organization, date range, state, and type. Includes attendance and verified-member counts.",
    path: "/reports/events-by-organization",
    icon: BuildingOffice2Icon,
  },
  {
    title: "Event Attendance & Close Rate",
    description:
      "Per-event attendance numbers, verified-member counts, and close-rate percentage.",
    path: "/reports/event-attendance",
    icon: ChartBarSquareIcon,
  },
  {
    title: "Verified Members",
    description:
      "Member-level detail: name, email, phone, member number, and verifying rep. Restricted to admin/manager roles.",
    path: "/reports/verified-members",
    icon: IdentificationIcon,
    isPii: true,
  },
  {
    title: "Sales by Rep",
    description:
      "Vouchers grouped by the credited sales rep on each CTA. Includes an Unattributed bucket for CTAs without a rep.",
    path: "/reports/sales-by-rep",
    icon: UserGroupIcon,
  },
  {
    title: "Sales Activity by Type",
    description:
      "Event counts and verified-member totals grouped by event type.",
    path: "/reports/sales-activity-by-type",
    icon: ClipboardDocumentCheckIcon,
  },
  {
    title: "Phone Sales",
    description:
      "Phone sales logged by reps. Member-level detail with PII. Restricted to admin/manager roles.",
    path: "/reports/phone-sales",
    icon: PhoneIcon,
    isPii: true,
  },
  {
    title: "Counter Sales",
    description:
      "Subscription-CTA voucher confirmations (no event context). Member-level detail with PII. Restricted to admin/manager.",
    path: "/reports/counter-sales",
    icon: ShoppingBagIcon,
    isPii: true,
  },
  {
    title: "Counter Sales — Rep × Month",
    description:
      "Counter sales aggregated by credited rep and month. No PII; commission-friendly view.",
    path: "/reports/counter-sales/aggregate",
    icon: ChartBarSquareIcon,
  },
  {
    title: "Conversion Summary by Event",
    description:
      "Conversion Engine funnel per event — vouchers issued/activated/redeemed, friends linked vs activated, attendance, and value.",
    path: "/reports/conversion-summary",
    icon: ChartBarSquareIcon,
  },
  {
    title: "Promoter Attendance & Fee Reconciliation",
    description:
      "Per promoter event — attendance, vouchers, friends, and value. Inputs for fee reconciliation (fee formula pending).",
    path: "/reports/promoter-fee-reconciliation",
    icon: BuildingOffice2Icon,
  },
  {
    title: "MSR Book of Business",
    description:
      "Conversions credited to each MSR — friends linked/activated, new members, and value. Credited to the booth rep with event-hierarchy fallback.",
    path: "/reports/msr-book-of-business",
    icon: UserGroupIcon,
  },
];

const Reports: React.FC = () => {
  const { user } = useAuth();
  // Smoke-test fix: opening /reports from another page sometimes preserves
  // the prior scroll position, hiding the cards behind the header.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Reports</h1>
        <p className="mt-1 text-sm text-gray-600">
          Filter, view, and export operational reports. Use the export button on
          each report to download as Excel.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        {REPORTS.filter((r) => !r.visible || r.visible(user?.role)).map((r) => {
          const Icon = r.icon;
          const cardInner = (
            <>
              <div className="flex items-start gap-3">
                <Icon className="h-8 w-8 text-blue-600 flex-shrink-0" />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="font-medium text-gray-900">{r.title}</h3>
                    {r.isPii && (
                      <span className="inline-flex items-center gap-1 rounded bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-800">
                        <LockClosedIcon className="h-3 w-3" />
                        PII
                      </span>
                    )}
                    {r.isComingSoon && (
                      <span className="inline-flex rounded bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                        Coming next
                      </span>
                    )}
                  </div>
                  <p className="mt-1 text-sm text-gray-600">{r.description}</p>
                </div>
              </div>
            </>
          );

          if (r.isComingSoon) {
            return (
              <div
                key={r.path}
                className="block rounded-lg border border-gray-200 bg-gray-50 p-4 opacity-60"
              >
                {cardInner}
              </div>
            );
          }
          return (
            <Link
              key={r.path}
              to={r.path}
              className="block rounded-lg border border-gray-200 bg-white p-4 transition hover:border-blue-300 hover:shadow-sm"
            >
              {cardInner}
            </Link>
          );
        })}
      </div>
    </div>
  );
};

export default Reports;
